/**
 * Device-Level Payment Support Detection
 *
 * Low-level functions to check if Apple Pay / Google Pay APIs are available
 * on the current device/browser. These are consumed by the gateway registry
 * (config/gatewayRegistry.ts) which provides the higher-level per-kind
 * `isPaymentMethodSupported()` and `getUnsupportedPaymentMessage()` helpers.
 *
 * Also exports mobile detection and version helpers used by the gateway
 * componentSelector to route payments on mobile (web processing vs native delegation).
 */

// ============================================================================
// Mobile App Detection Helpers
// ============================================================================

/**
 * Check if running inside a mobile app WebView.
 *
 * Mobile apps append `pg_client` or `client_platform` query params to the URL.
 * Matches the detection in mobileMessaging.ts and common/config/utils.js.
 */
export const isFromMobileApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  const search = new URLSearchParams(window.location.search);
  return !!(search.get('pg_client') || search.get('client_platform'));
};

export const isOldMobileApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  const search = new URLSearchParams(window.location.search);
  return search.get('pg_client') === 'pgapp';
};

/** Check if device is iOS */
const isIOS = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Compare app version from URL params against a required minimum version.
 * Returns true if the current app version >= requiredVersion.
 *
 * Reads `app_version` from URL query params (set by native app).
 */
export const isVersionHigherOrEqual = (requiredVersion: string): boolean => {
  if (typeof window === 'undefined') return false;

  const search = new URLSearchParams(window.location.search);
  const appVersion = search.get('app_version');

  if (!appVersion) return false;

  const required = requiredVersion.split('.').map(Number);
  const current = appVersion.split('.').map(Number);

  for (let i = 0; i < Math.max(required.length, current.length); i++) {
    const r = required[i] || 0;
    const c = current[i] || 0;
    if (c > r) return true;
    if (c < r) return false;
  }
  return true;
};

// ============================================================================
// Version Constants
// ============================================================================

const SUPPORTED_APPLE_PAY_VERSION = '5.4.0';
const SUPPORTED_GOOGLE_PAY_VERSION = '7.7.0';
const SUPPORTED_GOOGLE_PLAY_SERVICES_VERSION = 251831000;
const SUPPORTED_GOOGLE_PLAY_ANDROID_VERSION = 12;

/**
 * Minimum app version that supports web-based credit card processing.
 *
 * Below this version, credit card payments are delegated to the native app
 * via `on_pay` message (old flow behavior).
 *
 * At or above this version, credit card payments are processed on the web
 * and the result is sent to the app via `on_pay_completed` (like Apple Pay).
 *
 * TODO: Set the actual version agreed with the native app team.
 */
export const SUPPORTED_CC_WEB_PROCESSING_VERSION = '5.4.0';

// ============================================================================
// Apple Pay Support Detection
// ============================================================================

/**
 * Check if Apple Pay is supported on this device/browser.
 *
 * Uses window.config.appleypayMerchantId when present and runs canMakePaymentsWithActiveCard(merchantId) (aligns with checkIfCanMakeApplePayPayments). Otherwise falls back to sync canMakePayments().
 *
 * Requirements:
 * - window.ApplePaySession must exist
 * - For mobile app: version >= 5.4.0
 */
export const applePayIsSupported = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;

  const hasApplePaySession = Boolean(window.ApplePaySession);
  if (!hasApplePaySession) return false;

  const merchantId = window.config.appleypayMerchantId ?? null;
  if (!window.ApplePaySession || !merchantId) {
    return false;
  }

  try {
    const eligible = await window.ApplePaySession.canMakePaymentsWithActiveCard(merchantId);

    if (eligible && isFromMobileApp()) {
      return isVersionHigherOrEqual(SUPPORTED_APPLE_PAY_VERSION);
    }

    return eligible;
  } catch {
    return false;
  }
};

// ============================================================================
// Google Pay Support Detection
// ============================================================================

/**
 * Check if Google Pay is supported on this device/browser.
 *
 * Requirements:
 * - Web: Always supported (Google Pay JS handles browser compatibility)
 * - Mobile app: Not iOS + app version >= 7.7.0 + Google Play Services requirements
 */
// ============================================================================
// Google Pay Support Detection
// ============================================================================

export const googlePayIsSupported = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;

  // For web, Google Pay is generally supported
  if (!isFromMobileApp()) {
    return true;
  }

  // For mobile app: not supported on iOS
  if (isIOS()) {
    return false;
  }

  // Check app version requirement
  if (!isVersionHigherOrEqual(SUPPORTED_GOOGLE_PAY_VERSION)) {
    return false;
  }

  // Check Google Play Services version (Android only)
  const search = new URLSearchParams(window.location.search);
  const googlePlayVersion = Number(search.get('g_play_services_version') || 0);
  const operatingSystem = search.get('operating_system');
  const osVersion = Number(search.get('os_version') || 0);

  if (operatingSystem !== 'Android') {
    return false;
  }

  if (googlePlayVersion < SUPPORTED_GOOGLE_PLAY_SERVICES_VERSION) {
    return false;
  }

  if (osVersion < SUPPORTED_GOOGLE_PLAY_ANDROID_VERSION) {
    return false;
  }

  return true;
};
